import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import * as Lucide from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { MODULES } from "@/data/course";
import { LEVELS, ACHIEVEMENTS } from "@/data/levels";
import { Trophy, BookOpen, GraduationCap, Sparkles, Wallet, Lock } from "lucide-react";

export default function Progress() {
  const { user, progress, loadProgress } = useAuth();
  useEffect(() => { loadProgress(); /* eslint-disable-next-line */ }, []);

  if (!progress) return <div className="text-slate-400">Loading progress…</div>;

  const xp = progress.xp;
  const levelMin = progress.level_min || 0;
  const nextMin = progress.next_min;
  const pct = nextMin ? Math.min(100, Math.round(((xp - levelMin) / (nextMin - levelMin)) * 100)) : 100;
  const completed = progress.completed_modules || [];
  const quizCount = Object.keys(progress.quiz_scores || {}).length;

  const stats = [
    { icon: BookOpen, label: "Modules completed", value: `${completed.length}/15`, color: "text-blue-400" },
    { icon: GraduationCap, label: "Quizzes taken", value: quizCount, color: "text-violet-400" },
    { icon: Sparkles, label: "Exercises correct", value: `${progress.exercises_correct}/${progress.exercises_done}`, color: "text-amber-400" },
    { icon: Wallet, label: "Simulated trades", value: progress.simulated_trades ?? 0, color: "text-emerald-400" },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight">My Progress</h1>
        <p className="text-slate-400 mt-1">Track your journey, {user?.name}.</p>
      </div>

      {/* Level card */}
      <div className="bg-gradient-to-br from-slate-900 to-[#0B1120] border border-slate-800 rounded-2xl p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-2xl bg-amber-500/15 border border-amber-500/25 grid place-items-center">
              <Trophy className="w-7 h-7 text-amber-400" />
            </div>
            <div>
              <div className="text-xs font-mono uppercase tracking-wider text-slate-500">Current level</div>
              <div className="font-display text-2xl font-bold text-white" data-testid="progress-level">{progress.level}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="font-mono text-3xl font-bold text-blue-400" data-testid="progress-xp">{xp} XP</div>
            {progress.next_level && <div className="text-xs text-slate-500 font-mono">{nextMin - xp} XP to {progress.next_level}</div>}
          </div>
        </div>
        <div className="mt-5">
          <div className="h-2.5 bg-slate-800 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-blue-500 to-violet-500 transition-all" style={{ width: `${pct}%` }} />
          </div>
          <div className="flex justify-between mt-2">
            {LEVELS.map((l) => (
              <span key={l.name} className={`text-[10px] font-mono ${xp >= l.min ? "text-slate-300" : "text-slate-600"}`}>{l.name}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Course progress */}
      <div className="bg-[#0F172A] border border-slate-800 rounded-2xl p-6">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="text-slate-300 font-medium">Course completion</span>
          <span className="font-mono text-blue-400 font-semibold" data-testid="progress-course-percent">{progress.course_percent}%</span>
        </div>
        <div className="h-2.5 bg-slate-800 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-blue-500 to-emerald-500 transition-all" style={{ width: `${progress.course_percent}%` }} />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="bg-[#0F172A] border border-slate-800 rounded-2xl p-5">
              <Icon className={`w-5 h-5 ${s.color} mb-3`} />
              <div className={`font-mono text-2xl font-bold ${s.color}`}>{s.value}</div>
              <div className="text-xs text-slate-500 mt-0.5">{s.label}</div>
            </div>
          );
        })}
      </div>

      {/* Achievements */}
      <div>
        <h2 className="font-display text-xl font-semibold mb-3">Achievements</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {Object.entries(ACHIEVEMENTS).map(([key, a]) => {
            const unlocked = (progress.achievements || []).includes(key);
            const Icon = Lucide[a.icon] || Trophy;
            return (
              <div key={key} data-testid={`achievement-${key}`} className={`flex items-center gap-3 rounded-2xl border p-4 ${unlocked ? "border-amber-500/30 bg-amber-500/5" : "border-slate-800 bg-[#0F172A] opacity-60"}`}>
                <div className={`w-11 h-11 rounded-xl grid place-items-center shrink-0 ${unlocked ? "bg-amber-500/15" : "bg-slate-800"}`}>
                  {unlocked ? <Icon className="w-5 h-5 text-amber-400" /> : <Lock className="w-5 h-5 text-slate-600" />}
                </div>
                <div>
                  <div className={`font-semibold text-sm ${unlocked ? "text-slate-100" : "text-slate-500"}`}>{a.title}</div>
                  <div className="text-xs text-slate-500">{a.desc}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Completed modules */}
      <div>
        <h2 className="font-display text-xl font-semibold mb-3">Modules</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {MODULES.map((m) => {
            const done = completed.includes(m.id);
            return (
              <Link key={m.id} to={`/course/${m.id}`} className={`flex items-center gap-2.5 rounded-lg border px-3 py-2.5 text-sm transition-colors ${done ? "border-emerald-500/30 bg-emerald-500/5 text-slate-200" : "border-slate-800 bg-[#0F172A] text-slate-400 hover:border-blue-500/40"}`}>
                <span className={`w-5 h-5 rounded-full grid place-items-center text-[10px] font-mono font-bold shrink-0 ${done ? "bg-emerald-500 text-white" : "bg-slate-800 text-slate-500"}`}>{done ? "✓" : m.id}</span>
                <span className="truncate">{m.title}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
